import type { FormEvent } from "react";
import { useCallback, useMemo, useState } from "react";
import type { ZodError, ZodIssue } from "zod/v4";
import * as z from "zod/v4";
import { Card } from "./Card.tsx";

const NewAccountAttrs = z.object({
  name: z.string().trim().nonempty("You must provide a recognizable name for the account."),
});
type NewAccountAttrs = z.output<typeof NewAccountAttrs>;

export type NewAccountFormProps = {
  onSubmit?(newAccountAttrs: NewAccountAttrs): unknown | Promise<unknown>;
};

export function NewAccountForm(props: NewAccountFormProps) {
  const [error, setError] = useState<ZodError<z.output<typeof NewAccountAttrs>>>();
  const [isSaving, setIsSaving] = useState<boolean>();
  const fieldErrors = useMemo(() => {
    if (!error) return;

    const fieldErrors: Partial<Record<keyof NewAccountAttrs, ZodIssue>> = {};
    for (const issue of error.issues) {
      fieldErrors[issue.path.join(".") as keyof NewAccountAttrs] = issue;
    }
    return fieldErrors;
  }, [error]);

  const validateAndSubmitFormData = useCallback(
    async (submission: FormEvent<HTMLFormElement>): Promise<void> => {
      if (!submission.defaultPrevented) submission.preventDefault();

      const formData = new FormData(submission.currentTarget);

      const newAccountAttrsParsingResult = NewAccountAttrs.safeParse({
        name: formData.get("name"),
      });

      if (!newAccountAttrsParsingResult.success) {
        setError(newAccountAttrsParsingResult.error);
        return;
      } else {
        setError(undefined);
      }

      const newAccountAttrs = newAccountAttrsParsingResult.data;

      if (typeof props.onSubmit !== "function") {
        console.debug(
          `Would have called onSubmit with %O if props.onSubmit was a function`,
          newAccountAttrs,
        );
        return;
      }

      const result = props.onSubmit(newAccountAttrs);

      if (result instanceof Promise) {
        setIsSaving(true);
        result.then(
          () => {
            // TODO: Clear form fields
            setIsSaving(false);
          },
          (err: unknown) => {
            console.warn(`Error submitting new account: ${err}`);
            setIsSaving(false);
          },
        );
      }
    },
    [setError],
  );

  return (
    <Card style={{ maxWidth: "800px", minWidth: "600px" }} title={<h3>Create a new account</h3>}>
      <form onSubmit={validateAndSubmitFormData}>
        <div className="form-control">
          <label htmlFor="name">Account name</label>
          <input
            aria-describedby="name-validation-message"
            aria-invalid={fieldErrors?.name !== undefined ? true : undefined}
            aria-label="Account name"
            name="name"
            placeholder="Emergency savings"
            type="text"
            width="100%"
          />
          {fieldErrors?.name && (
            <small className="error" id="name-validation-message">
              {fieldErrors.name.message}
            </small>
          )}
        </div>
        <button aria-busy={isSaving} className="primary" disabled={isSaving} type="submit">
          Create
        </button>
      </form>
    </Card>
  );
}
